import { CSSProperties } from 'react'
import styles from './loading-skeleton.module.css'

export interface LoadingSkeletonProps {
  variant?: 'text' | 'circular' | 'rectangular'
  width?: number | string
  height?: number | string
  borderRadius?: number | string
  className?: string
  style?: CSSProperties
}

export function LoadingSkeleton({
  variant = 'text',
  width,
  height,
  borderRadius,
  className,
  style,
}: LoadingSkeletonProps) {
  const variantClass = styles[variant] || styles.text

  const inlineStyle: CSSProperties = {
    ...style,
  }

  if (width !== undefined) {
    inlineStyle.width = typeof width === 'number' ? `${width}px` : width
  }

  if (height !== undefined) {
    inlineStyle.height = typeof height === 'number' ? `${height}px` : height
  }

  if (borderRadius !== undefined) {
    inlineStyle.borderRadius = typeof borderRadius === 'number' ? `${borderRadius}px` : borderRadius
  }

  return (
    <div className={`${styles.skeleton} ${variantClass} ${className || ''}`} style={inlineStyle} aria-hidden="true" />
  )
}

// Pre-built skeleton components
interface SkeletonCardProps {
  avatar?: boolean
  lines?: number
  className?: string
}

export function SkeletonCard({ avatar = true, lines = 3, className }: SkeletonCardProps) {
  return (
    <div className={`${styles.card} ${className || ''}`}>
      {avatar && (
        <div className={styles.cardHeader}>
          <LoadingSkeleton variant="circular" width={40} height={40} />
          <div className={styles.cardMeta}>
            <LoadingSkeleton variant="text" width="60%" height={14} />
            <LoadingSkeleton variant="text" width="40%" height={12} />
          </div>
        </div>
      )}
      <div className={styles.cardBody}>
        {Array.from({ length: lines }).map((_, i) => (
          <LoadingSkeleton
            key={i}
            variant="text"
            width={i === lines - 1 ? '70%' : '100%'}
            height={12}
          />
        ))}
      </div>
    </div>
  )
}

interface SkeletonListProps {
  count?: number
  className?: string
}

export function SkeletonList({ count = 5, className }: SkeletonListProps) {
  return (
    <div className={`${styles.list} ${className || ''}`}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className={styles.listItem}>
          <LoadingSkeleton variant="circular" width={36} height={36} />
          <div className={styles.listContent}>
            <LoadingSkeleton variant="text" width="70%" height={14} />
            <LoadingSkeleton variant="text" width="50%" height={12} />
          </div>
        </div>
      ))}
    </div>
  )
}

interface SkeletonParagraphProps {
  lines?: number
  className?: string
}

export function SkeletonParagraph({ lines = 4, className }: SkeletonParagraphProps) {
  return (
    <div className={`${styles.paragraph} ${className || ''}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <LoadingSkeleton
          key={i}
          variant="text"
          width={i === lines - 1 ? '60%' : '100%'}
          height={14}
        />
      ))}
    </div>
  )
}

// Compound component for full page loading
interface SkeletonPageProps {
  className?: string
}

export function SkeletonPage({ className }: SkeletonPageProps) {
  return (
    <div className={`${styles.page} ${className || ''}`}>
      <div className={styles.pageHeader}>
        <LoadingSkeleton variant="rectangular" width={120} height={24} borderRadius={4} />
      </div>
      <div className={styles.pageContent}>
        {Array.from({ length: 3 }).map((_, i) => (
          <SkeletonCard key={i} avatar={false} lines={2} />
        ))}
      </div>
    </div>
  )
}